import { useEffect, useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, Typography, Table, Tag, Button, Modal, Form, Select, InputNumber, Input, DatePicker, Space, Alert, message, Tooltip, Avatar, Tabs, Badge } from '../../ui';
import { Plus, Pencil, CheckCircle2, Search, ArrowLeftRight, Upload } from 'lucide-react';
import dayjs from 'dayjs';
import { request } from '../../api/client';
import { formatIstDate } from '../../lib/time';
import { usePermission, useAnyPermission } from '../../store/auth';
import { fmtQty } from '../../lib/format';
import { TransfersPage } from './TransfersPage';
import { AdjustmentsPage } from './AdjustmentsPage';
import { TransferFormModal } from './TransferFormModal';
import { AdjustmentFormModal } from './AdjustmentFormModal';
import { BulkImportModal } from '../../components/BulkImportModal';
import { TemplateButton } from '../../components/TemplateButton';
import { useBadges } from '../../hooks/useBadges';
import { useSearchParams } from 'react-router-dom';

// Batch-status → tag colour. This is the physical inventory view, so it shows EVERY on-hand batch regardless of
// sellability; colouring the non-AVAILABLE states (instead of a flat grey) makes expired / held / blocked stock
// read as what it is — something to action (e.g. write off an expired lot via an adjustment).
const STATUS_COLOR: Record<string, string> = { AVAILABLE: 'green', EXPIRED: 'red', QC_HOLD: 'orange', BLOCKED: 'volcano' };

/**
 * Master-warehouse opening stock — the single stock-origin point. Add product + qty (auto-generated,
 * editable batch number) which seeds the actual stock; everything else flows out via transfers.
 */
// Warehouse-operations hub: master opening stock + transfers + adjustments in one place.
export function MasterStockPage() {
  const canMaster = usePermission('STOCK.MASTER.ADD');
  const canTransferTab = useAnyPermission(['STOCK.VIEW', 'STOCK.TRANSFER']);
  const canAdjustTab = useAnyPermission(['STOCK.VIEW', 'STOCK.ADJUST.CREATE', 'STOCK.ADJUST.APPROVE']);
  const canCreateTransfer = usePermission('STOCK.TRANSFER');
  const canCreateAdjust = usePermission('STOCK.ADJUST.CREATE');
  const [transferOpen, setTransferOpen] = useState(false);
  const [adjustOpen, setAdjustOpen] = useState(false);
  // Live "needs action" counts on the tabs, same source as the navbar badges.
  const badges = useBadges();
  const transfersBadge = (badges.transfersAwaitingReceipt ?? 0) + (badges.transfersAwaitingDispatch ?? 0);
  const adjustBadge = badges.adjustmentsPendingApproval ?? 0;

  // Master Stock tab is admin-only (STOCK.MASTER.ADD); a Warehouse Manager sees only Transfers/Adjustments.
  const items = [
    ...(canMaster ? [{ key: 'master', label: 'Master Stock', children: <MasterStockTab /> }] : []),
    ...(canTransferTab ? [{ key: 'transfers', label: (<span>Transfers{transfersBadge ? <Badge count={transfersBadge} size="small" className="ms-1.5" /> : null}</span>), children: <TransfersPage embedded /> }] : []),
    ...(canAdjustTab ? [{ key: 'adjustments', label: (<span>Adjustments{adjustBadge ? <Badge count={adjustBadge} size="small" className="ms-1.5" /> : null}</span>), children: <AdjustmentsPage embedded /> }] : []),
  ];
  // URL-driven active tab (?tab=) so the old /inventory/transfers + /inventory/adjustments links land on the right tab.
  const [sp, setSp] = useSearchParams();
  const tabParam = sp.get('tab') || '';
  const activeKey = items.some((i) => i.key === tabParam) ? tabParam : items[0]?.key;
  const onTab = (k: string) => { const next = new URLSearchParams(sp); next.set('tab', k); setSp(next, { replace: true }); };

  return (
    <>
      <Tabs
        activeKey={activeKey}
        onChange={onTab}
        tabBarExtraContent={
          (canCreateTransfer || canCreateAdjust) ? (
            <Space>
              {canCreateTransfer && (
                <Button variant="ghost" icon={<ArrowLeftRight size={16} />} onClick={() => setTransferOpen(true)}>New Transfer</Button>
              )}
              {canCreateAdjust && (
                <Button variant="primary" icon={<Plus size={16} />} onClick={() => setAdjustOpen(true)}>New adjustment</Button>
              )}
            </Space>
          ) : undefined
        }
        items={items}
      />
      <TransferFormModal open={transferOpen} onClose={() => setTransferOpen(false)} />
      <AdjustmentFormModal open={adjustOpen} onClose={() => setAdjustOpen(false)} />
    </>
  );
}

function MasterStockTab() {
  const qc = useQueryClient();
  const canAdd = usePermission('STOCK.MASTER.ADD');
  const canImport = usePermission('GRN.CREATE');

  const [productId, setProductId] = useState<string | undefined>();
  const [batchNo, setBatchNo] = useState('');
  // "In stock" means SELLABLE (matches the rest of the app); expired/held/blocked live under the other scopes.
  const [scope, setScope] = useState<'sellable' | 'nonsellable' | 'all' | 'depleted'>('sellable');
  const [addOpen, setAddOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [editRow, setEditRow] = useState<any>(null);

  // Active products only — archived/deactivated SKUs must not appear in the opening-stock picker.
  const { data: products } = useQuery({ queryKey: ['products', 'active'], queryFn: () => request<any>({ url: '/products', params: { pageSize: 500, active: 'true' } }) });
  const productOpts = useMemo(() => (products?.data ?? []).map((p: any) => ({ value: String(p.id), label: `${p.sku} · ${p.name}` })), [products]);

  const params = useMemo(() => {
    const p: Record<string, string> = { scope };
    if (productId) p.productId = productId;
    if (batchNo.trim()) p.batchNo = batchNo.trim();
    return p;
  }, [productId, batchNo, scope]);

  const { data, isFetching } = useQuery({ queryKey: ['master-stock', params], queryFn: () => request<any>({ url: '/master-stock', params }) });
  const master = data?.master ?? null;
  const rows = data?.rows ?? [];

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['master-stock'] });
    qc.invalidateQueries({ queryKey: ['stock'] });
  };

  const columns = [
    { title: 'Batch No', dataIndex: 'batchNo', render: (v: string, r: any) => (
      <div>
        <Space>{v}{!r.editable && <Tag>locked</Tag>}</Space>
        {r.sourceBatchNo && <div className="mt-0.5 text-[11px] text-subtle" title="Original printed batch this returned/quarantined stock came from">↩ from {r.sourceBatchNo}</div>}
      </div>
    ) },
    { title: 'Product', dataIndex: 'productName', render: (_: any, r: any) => `${r.sku} · ${r.productName}` },
    { title: 'Available', dataIndex: 'available', align: 'right' as const, render: (v: any) => <b>{fmtQty(v)}</b> },
    { title: 'In', dataIndex: 'qtyIn', align: 'right' as const, render: (v: any) => fmtQty(v) },
    { title: 'Out', dataIndex: 'qtyOut', align: 'right' as const, render: (v: any) => fmtQty(v) },
    { title: 'Mfg', dataIndex: 'mfgDate', render: (v: any) => formatIstDate(v) },
    { title: 'Exp', dataIndex: 'expDate', render: (v: any) => formatIstDate(v) },
    { title: 'Status', dataIndex: 'status', render: (v: string) => <Tag color={STATUS_COLOR[v] ?? 'default'}>{v}</Tag> },
    { title: 'Added', dataIndex: 'addedAt', render: (v: string) => (v ? dayjs(v).format('DD MMM YYYY') : '') },
    ...(canAdd ? [{
      title: '', key: 'act', width: 56,
      render: (_: any, r: any) => (
        <Tooltip label={r.editable ? 'Edit batch (number / dates)' : 'Batch has moved — correct via a stock adjustment'}>
          <Button size="sm" variant="default" aria-label="Edit batch" icon={<Pencil size={16} />} disabled={!r.editable} onClick={() => setEditRow(r)} />
        </Tooltip>
      ),
    }] : []),
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        {master && <Tag color="gold">Master warehouse: {master.code} · {master.name}</Tag>}
      </div>

      {!master && (
        <Alert type="warning" style={{ marginBottom: 12 }}
          title="No master warehouse set"
          description="Open Warehouses and turn on “Master warehouse” for one location to start adding opening stock." />
      )}

      <Card>
        <Space wrap style={{ marginBottom: 12 }}>
          <Select allowClear showSearch optionFilterProp="label" placeholder="Product" style={{ width: 260 }}
            value={productId} onChange={setProductId} options={productOpts} />
          <Input allowClear placeholder="Batch no" style={{ width: 180 }} value={batchNo} onChange={(e) => setBatchNo(e.target.value)} />
          <Select style={{ width: 170 }} value={scope} onChange={(v) => setScope(v as any)}
            options={[
              { value: 'sellable', label: 'In stock' },
              { value: 'nonsellable', label: 'Non-sellable' },
              { value: 'all', label: 'All on-hand' },
              { value: 'depleted', label: 'Include depleted' },
            ]} />
          {canAdd && master && (
            <Button variant="primary" icon={<Plus size={16} />} onClick={() => setAddOpen(true)}>Add stock</Button>
          )}
          {canImport && (
            <Button icon={<Upload size={16} />} onClick={() => setImportOpen(true)}>Import opening stock</Button>
          )}
          {canImport && <TemplateButton kind="opening-stock" />}
        </Space>
        <Table size="small" rowKey="id" loading={isFetching} dataSource={rows} columns={columns}
          pagination={{ pageSize: 25, showSizeChanger: true }} />
      </Card>

      <AddStockModal open={addOpen} onClose={() => setAddOpen(false)} products={products?.data ?? []} onDone={refresh} />
      <EditBatchModal row={editRow} onClose={() => setEditRow(null)} onDone={refresh} />
      <BulkImportModal open={importOpen} onClose={() => setImportOpen(false)} onDone={refresh}
        kind="opening-stock" entityPlural="opening-stock rows" columns="sku, warehouseCode, batchNo, mfgDate, expDate, qty" />
    </div>
  );
}

function AddStockModal({ open, onClose, products, onDone }: { open: boolean; onClose: () => void; products: any[]; onDone: () => void }) {
  const [form] = Form.useForm();
  const [shelfLife, setShelfLife] = useState<number | null>(null);
  const selectedProductId = Form.useWatch('productId', form);

  // Apply a chosen product: pre-fill batch no, default Mfg to today, set Exp = Mfg + shelf life.
  const applyProduct = (productId?: string) => {
    form.setFieldsValue({ productId });
    if (!productId) return;
    request<any>({ url: '/master-stock/next-batch-no', params: { productId } })
      .then((r) => {
        setShelfLife(r?.shelfLifeMonths ?? null);
        const all = form.getFieldsValue();
        const mfg = all.mfgDate ?? dayjs();
        const patch: any = { batchNo: r?.batchNo ?? '' };
        if (!all.mfgDate) patch.mfgDate = mfg;
        if (r?.shelfLifeMonths != null) patch.expDate = mfg.add(Number(r.shelfLifeMonths), 'month');
        form.setFieldsValue(patch);
      })
      .catch(() => { /* leave blank → backend auto-generates on submit */ });
  };

  const save = useMutation({
    mutationFn: (vals: any) => request({
      url: '/master-stock', method: 'POST', data: {
        productId: vals.productId, qty: Number(vals.qty),
        batchNo: vals.batchNo?.trim() || undefined,
        mfgDate: vals.mfgDate ? vals.mfgDate.format('YYYY-MM-DD') : undefined,
        expDate: vals.expDate ? vals.expDate.format('YYYY-MM-DD') : undefined,
      },
    }),
    onSuccess: () => { message.success('Stock added to master warehouse'); form.resetFields(); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title="Add opening stock" width={620}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Add stock</Button></>}>
      <Form form={form} layout="vertical" onFinish={(v) => save.mutate(v)}
        onValuesChange={(changed) => {
          // Recompute expiry whenever the manufacturing date changes.
          if (changed.mfgDate && shelfLife != null) form.setFieldsValue({ expDate: changed.mfgDate.add(shelfLife, 'month') });
        }}>
        <Form.Item label="Product" required style={{ marginBottom: 12 }}>
          <ProductPicker products={products} value={selectedProductId} onChange={applyProduct} />
        </Form.Item>
        <Form.Item name="productId" rules={[{ required: true, message: 'Pick a product' }]} hidden>
          <Input />
        </Form.Item>
        <Form.Item name="qty" label="Quantity" rules={[{ required: true }]}>
          <InputNumber style={{ width: '100%' }} min={1} step={1} precision={0} />
        </Form.Item>
        <Form.Item name="batchNo" label="Batch number" tooltip="Auto-filled from the product when you pick it — edit it here if you want a different number.">
          <Input placeholder="Pick a product to auto-fill" />
        </Form.Item>
        <Space size="large">
          <Form.Item name="mfgDate" label="Mfg date"><DatePicker /></Form.Item>
          <Form.Item name="expDate" label="Exp date" tooltip="Auto-set to Mfg date + the product's shelf life — edit if needed.">
            <DatePicker />
          </Form.Item>
        </Space>
      </Form>
    </Modal>
  );
}

function EditBatchModal({ row, onClose, onDone }: { row: any; onClose: () => void; onDone: () => void }) {
  const [form] = Form.useForm();
  const open = !!row;
  // Seed via initialValues + a per-row key (below), not a setFieldsValue open-effect that races the
  // Radix-modal remount (blank fields / a prior batch's values lingering).
  const save = useMutation({
    mutationFn: (vals: any) => request({
      url: `/master-stock/${row.id}`, method: 'PATCH', data: {
        batchNo: vals.batchNo?.trim(),
        mfgDate: vals.mfgDate ? vals.mfgDate.format('YYYY-MM-DD') : null,
        expDate: vals.expDate ? vals.expDate.format('YYYY-MM-DD') : null,
      },
    }),
    onSuccess: () => { message.success('Batch updated'); onDone(); onClose(); },
    onError: (e: any) => message.error(e.response?.data?.error?.message ?? 'Failed'),
  });
  return (
    <Modal open={open} onOpenChange={(o) => { if (!o) onClose(); }} title={`Edit batch · ${row?.batchNo ?? ''}`}
      footer={<><Button variant="default" onClick={onClose}>Cancel</Button><Button variant="primary" loading={save.isPending} onClick={() => form.submit()}>Save</Button></>}>
      <Form form={form} key={`edit-${row?.id ?? 'none'}`} layout="vertical"
        initialValues={{ batchNo: row?.batchNo, mfgDate: row?.mfgDate ? dayjs(row.mfgDate) : null, expDate: row?.expDate ? dayjs(row.expDate) : null }}
        onFinish={(v) => save.mutate(v)}>
        <Form.Item name="batchNo" label="Batch number" rules={[{ required: true }]}><Input /></Form.Item>
        <Space size="large">
          <Form.Item name="mfgDate" label="Mfg date"><DatePicker /></Form.Item>
          <Form.Item name="expDate" label="Exp date"><DatePicker /></Form.Item>
        </Space>
        <Typography.Paragraph type="secondary" style={{ margin: 0 }}>Quantity corrections are done via Adjustments.</Typography.Paragraph>
      </Form>
    </Modal>
  );
}

// Two-step product picker: choose the product family (AGN / AGN LTE), then its variant — clean cards.
const PICKER_PALETTE = ['#389e0d', '#1677ff', '#722ed1', '#c41d7f', '#08979c', '#d46b08'];
const pickInitials = (s: string) => (s.match(/[A-Za-z0-9]+/g) ?? []).map((w) => w[0]).join('').slice(0, 2).toUpperCase() || '?';
const pickColor = (s: string) => PICKER_PALETTE[(s.charCodeAt(0) || 0) % PICKER_PALETTE.length];

function ProductPicker({ products, value, onChange }: { products: any[]; value?: string; onChange: (productId: string) => void }) {
  const [q, setQ] = useState('');
  const groups = useMemo(() => {
    const m = new Map<string, any[]>();
    for (const p of products) {
      const k = p.name ?? '—';
      if (!m.has(k)) m.set(k, []);
      m.get(k)!.push(p);
    }
    return m;
  }, [products]);
  const bases = useMemo(() => [...groups.keys()], [groups]);
  const [base, setBase] = useState<string | undefined>(undefined);

  // Keep the open family in sync with an externally-set value; auto-open when there's only one.
  useEffect(() => {
    if (value) {
      const p = products.find((x) => String(x.id) === String(value));
      if (p?.name) { setBase(p.name); return; }
    }
    if (bases.length === 1) setBase(bases[0]);
  }, [value, products, bases]);

  if (!products.length) return <Typography.Text type="secondary">No products available</Typography.Text>;

  const s = q.trim().toLowerCase();
  const visibleBases = s
    ? bases.filter((n) => n.toLowerCase().includes(s) || (groups.get(n) ?? []).some((p: any) => String(p.sku ?? '').toLowerCase().includes(s) || String(p.variantLabel ?? '').toLowerCase().includes(s)))
    : bases;
  const variants = base ? (groups.get(base) ?? []) : [];
  const cardSel = (active: boolean) => ({ cursor: 'pointer', borderColor: active ? '#389e0d' : undefined, background: active ? '#f6ffed' : undefined });

  return (
    <div>
      <Input allowClear placeholder="Search product or SKU…" prefix={<Search size={16} style={{ color: '#bbb' }} />}
        value={q} onChange={(e) => setQ(e.target.value)} style={{ marginBottom: 10 }} />

      {/* Step 1 — product family */}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>1 · Product</Typography.Text>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: 8, margin: '6px 0 4px' }}>
        {visibleBases.map((name) => {
          const list = groups.get(name)!;
          return (
            <Card key={name} hoverable style={cardSel(base === name)} onClick={() => setBase(name)}>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                <Avatar style={{ background: pickColor(name), flexShrink: 0, fontSize: 13 }} fallback={pickInitials(name)} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</div>
                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>{list.length} variant{list.length === 1 ? '' : 's'}</Typography.Text>
                </div>
              </div>
            </Card>
          );
        })}
        {!visibleBases.length && <Typography.Text type="secondary" style={{ gridColumn: '1 / -1' }}>No matching product</Typography.Text>}
      </div>

      {/* Step 2 — variant */}
      {base && (
        <div style={{ marginTop: 8 }}>
          <Typography.Text type="secondary" style={{ fontSize: 12 }}>2 · Variant of <b>{base}</b></Typography.Text>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(185px, 1fr))', gap: 8, marginTop: 6, maxHeight: 196, overflowY: 'auto', paddingRight: 4 }}>
            {variants.map((p: any) => {
              const active = String(value) === String(p.id);
              const label = p.variantLabel || p.name;
              return (
                <Card key={p.id} hoverable style={cardSel(active)} onClick={() => onChange(String(p.id))}>
                  <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                    <Avatar style={{ background: pickColor(String(label)), flexShrink: 0, fontSize: 13 }} fallback={pickInitials(`${p.name} ${p.variantLabel ?? ''}`)} />
                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{label}</div>
                      <Typography.Text type="secondary" style={{ fontSize: 12 }}>{p.sku}</Typography.Text>
                    </div>
                    {active && <CheckCircle2 size={16} style={{ color: '#389e0d', flexShrink: 0 }} />}
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
